import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box, ActionMenu, ActionList, IconButton, Checkbox, FormControl } from "@primer/react";
import {
  TrashIcon,
  CheckIcon,
  KebabHorizontalIcon,
  PencilIcon,
  XIcon,
} from "@primer/octicons-react";
import { hinhThucThanhToanApi } from "../../../api/hinhThucThanhToanApi";
import { mucPhaiThuApi } from "../../../api/mucPhaiThuApi";
import { phanBoPhiApi } from "../../../api/phanBoPhiApi";
import { showNotify } from "../../../helpers/toast";
import { PageBaseStatus } from "../../../models/pageBaseStatus";
import { sf_mucphaithu_select_byhocsinh_rq } from "../../../models/request/thu-phi/sf_mucphaithu_select_byhocsinh_rq";
import { sf_hinhthucthanhtoan } from "../../../models/request/hinh-thuc-thanh-toan/sf_hinhthucthanhtoan";
import { ts_hocsinh } from "../../../models/response/hocsinh/ts_hocsinh";
import { sf_mucphaithu } from "../../../models/response/thu-phi/sf_mucphaithu";
import { RootState } from "../../../state/reducers";
import "../hinh-thuc-thu-phi/style.css";
import "./style.css";
import DataGrid from "../../data-grid";
import { IDataGridColumn } from "../../data-grid/type/IDataGridProps";
import Button from "../../ui/button";
import NumberInput from "../../ui/number-input";
import TextInput from "../../ui/text-input";
import TextWithEllipse from "../../ui/text-with-ellipse";
import DateInput from "../../ui/date-input";

type PhanBoPhiGridItem = {
  id?: number;
  so_tien?: number;
  ngay_thu?: string;
  ghi_chu?: string;
  sf_hinhthucthanhtoan_id?: number;
  hinh_thuc_thanh_toan?: string;
  loai_hinh_thuc?: number;
  phi_quet_the?: number;
  so_tien_phi_quet_the?: number;
  is_cong_phi_quet_the?: boolean;
};

type HinhThucThanhToanItem = sf_hinhthucthanhtoan & {
  loai_hinh_thuc?: number;
  phi_quet_the?: number;
};

export type ThuPhiThongTinTongHopProps = {
  sf_mucphaithu: sf_mucphaithu;
  ts_hocsinh: ts_hocsinh;
  sf_phanbophis?: any[];
  dataSource?: any[];
  sf_bienlaithu?: any[];
  onHinhThucClick?: (hinhThuc: any) => void;
  tong_tien_thua?: number;
  tong_tien_da_nop_custom?: number;
  onPhanBoPhiChanged?: () => void;
  ngaythu?: string;
  onNgayThuChange?: (newDate: string) => void;
  onUpdateNgayThu?: (newDate: string) => void;
  onNoiDungChange?: (newNoiDung: string) => void;
  onUpdateNoiDung?: (newNoiDung: string) => void | Promise<void>;
};

const ThuPhiThongTinTongHop = (props: ThuPhiThongTinTongHopProps) => {
  const { dm_coso_id, nam_hoc } = useSelector((x: RootState) => x.common);
  const [sf_hinhthucthanhtoans, set_sf_hinhthucthanhtoans] = useState<
    HinhThucThanhToanItem[]
  >([]);
  const [selectedHinhThuc, setSelectedHinhThuc] = useState<any>(null);
  const [selected, setSelected] = useState<any>([]);
  const [selectedTienThua, setSelectedTienThua] = useState<any>([]);
  const [status, setStatus] = useState<string>(PageBaseStatus.is_completed);
  const [sf_phanbophis, set_sf_phanbophis] = useState<any[]>([]);
  const [tong_tien_thua, set_tong_tien_thua] = useState<any>(0);
  const [tong_tien_thua_temp, set_tong_tien_thua_temp] = useState<any>(0);
  const [tong_tien_tra_truoc, set_tong_tien_tra_truoc] = useState<any>(0);
  const [isEditingNgayThu, setIsEditingNgayThu] = useState<boolean>(false);
  const [draftNgayThu, setDraftNgayThu] = useState<string>("");
  const [isEditingNoiDung, setIsEditingNoiDung] = useState<boolean>(false);
  const [draftNoiDung, setDraftNoiDung] = useState<string>("");
  const [originalHinhThucId, setOriginalHinhThucId] = useState<number | null>(null);

  const soTienThuaHienThi =
    (props.tong_tien_thua ?? tong_tien_thua) -
    (props.tong_tien_da_nop_custom ?? 0);
  const hasBienLai = !!(props.sf_bienlaithu && props.sf_bienlaithu.length > 0);

  const resetSelection = useCallback(() => {
    setSelected(null);
    setSelectedTienThua({ so_tien: 0 });
    setSelectedHinhThuc(null);
    setOriginalHinhThucId(null);
  }, []);

  const realDataSource = useMemo(
    () => (props.dataSource ?? []).filter((x: any) => !x.isGroupHeader),
    [props.dataSource]
  );

  const tongTienDinhMuc = useMemo(
    () =>
      realDataSource.length > 0
        ? realDataSource.reduce(
          (sum, item) => sum + (Number(item.so_tien_dinh_muc) || 0),
          0
        )
        : props.sf_mucphaithu?.tong_tien_dinh_muc || 0,
    [realDataSource, props.sf_mucphaithu]
  );

  const tongTienMienGiam = useMemo(
    () => props.sf_mucphaithu?.tong_tien_mien_giam || 0,
    [props.sf_mucphaithu]
  );

  const tongTienPhaiNop = useMemo(
    () =>
      realDataSource.length > 0
        ? realDataSource.reduce(
          (sum, item) => sum + (Number(item.so_tien_phai_nop) || 0),
          0
        )
        : props.sf_mucphaithu?.tong_tien_phai_nop || 0,
    [realDataSource, props.sf_mucphaithu]
  );

  const tongDaNopPhanBo = useMemo(
    () =>
      sf_phanbophis.reduce(
        (sum: number, item: any) => {
          const isCongPhi = !!item.is_cong_phi_quet_the;
          const phiVal = isCongPhi ? (Number(item.so_tien_phi_quet_the ?? item.phi_quet_the) || 0) : 0;
          return sum + (Number(item.so_tien) || 0) + phiVal;
        },
        0
      ),
    [sf_phanbophis]
  );

  const tongPhiQuetThe = useMemo(
    () =>
      sf_phanbophis.reduce(
        (sum: number, item: any) => {
          const isCongPhi = !!item.is_cong_phi_quet_the;
          const phiVal = isCongPhi ? (Number(item.so_tien_phi_quet_the ?? item.phi_quet_the) || 0) : 0;
          return sum + phiVal;
        },
        0
      ),
    [sf_phanbophis]
  );

  const tongTienConLai = useMemo(
    () => Math.max(tongTienPhaiNop - tongDaNopPhanBo, 0),
    [tongTienPhaiNop, tongDaNopPhanBo]
  );

  const handleReloadHinhThucThanhToan = useCallback(async () => {
    const res = await hinhThucThanhToanApi.select_all();
    set_sf_hinhthucthanhtoans(
      res.data.filter((x: any) => x.dm_coso_id === dm_coso_id)
    );
  }, [dm_coso_id]);

  useEffect(() => {
    handleReloadHinhThucThanhToan();
  }, [handleReloadHinhThucThanhToan]);

  const handleReloadPhanBoPhi = useCallback(async () => {
    const res = await phanBoPhiApi.select_by_sct(props.sf_mucphaithu.id);
    const transformed = (res.data || []).map((item: any) => {
      // Nếu là trường hợp cộng phí, thì so_tien từ API là tổng tiền.
      // Cần trừ đi phí để ra số tiền gốc (revenue/base) cho logic hiển thị và tính toán frontend
      if (item.is_cong_phi_quet_the && item.so_tien_phi_quet_the) {
        return {
          ...item,
          so_tien:
            (Number(item.so_tien) || 0) - (Number(item.so_tien_phi_quet_the) || 0),
        };
      }
      return item;
    });
    set_sf_phanbophis(transformed);
  }, [props.sf_mucphaithu.id]);

  const handleReloadTienThuaHocSinh = useCallback(async () => {
    const datasend: sf_mucphaithu_select_byhocsinh_rq = {
      ts_hocsinh_id: props.ts_hocsinh.id,
      nam_hoc: nam_hoc,
    };
    const res = await mucPhaiThuApi.select_tienthua_by_hocsinh(datasend);
    if (res.is_success && res.data && res.data.tong_tien_thua !== undefined) {
      set_tong_tien_thua(res.data.tong_tien_thua);
    }
  }, [props.ts_hocsinh.id, nam_hoc]);

  useEffect(() => {
    handleReloadPhanBoPhi();
    handleReloadTienThuaHocSinh();
  }, [handleReloadPhanBoPhi, handleReloadTienThuaHocSinh]);

  useEffect(() => {
    if (tong_tien_thua && selectedTienThua.so_tien >= 0) {
      set_tong_tien_thua_temp(tong_tien_thua - selectedTienThua.so_tien);
    } else {
      set_tong_tien_thua_temp(tong_tien_thua);
    }
  }, [tong_tien_thua, selectedTienThua]);

  useEffect(() => {
    if (sf_phanbophis.length > 0) {
      const tong_tien_tra_truoc = sf_phanbophis
        .filter((x: any) =>
          x.hinh_thuc_thanh_toan.toLowerCase().includes("trả trước")
        )
        .reduce((sum: number, item: any) => sum + item.so_tien, 0);
      set_tong_tien_tra_truoc(tong_tien_tra_truoc);
    } else {
      set_tong_tien_tra_truoc(0);
    }
  }, [sf_phanbophis]);

  useEffect(() => {
    const isoValue = props.ngaythu ? moment(props.ngaythu).format("YYYY-MM-DD") : "";
    setDraftNgayThu(isoValue);
  }, [props.ngaythu]);

  useEffect(() => {
    setDraftNoiDung(props.sf_mucphaithu?.noi_dung || "");
  }, [props.sf_mucphaithu?.noi_dung]);

  useEffect(() => {
    console.log("selected", selected);
  }, [selected]);

  const onHinhThucClick = (hinhThuc: any) => {
    // Nếu click vào chính hình thức đang được chọn để edit thì không làm gì để tránh mất data đang nhập dở
    if (selected && selected.sf_hinhthucthanhtoan_id === hinhThuc.id) {
      return;
    }

    setSelectedHinhThuc(hinhThuc);

    // Nếu có biên lai và đang edit một bản ghi, và click vào một hình thức CHƯA có bản ghi, 
    // thì mới thực hiện đổi hình thức cho bản ghi hiện tại.
    // Ngược lại nếu click vào hình thức ĐÃ CÓ bản ghi (phanBoInfo), thì phải chuyển sang bản ghi đó.
    if (hasBienLai && selected && selected.id && !hinhThuc.phanBoInfo) {
      const soTienHienTai = Number(selected.so_tien) || 0;
      const soTienPhiQuetThe =
        hinhThuc.loai_hinh_thuc === 3
          ? selected?.so_tien_phi_quet_the ??
          Math.round(soTienHienTai * ((hinhThuc.phi_quet_the || 0) / 100))
          : 0;
      console.log("soTienPhiQuetThe", soTienPhiQuetThe);
      setSelected({
        ...selected,
        sf_hinhthucthanhtoan_id: hinhThuc.id,
        hinh_thuc_thanh_toan: hinhThuc.hinh_thuc_thanh_toan,
        loai_hinh_thuc: hinhThuc.loai_hinh_thuc,
        phi_quet_the: hinhThuc.phi_quet_the,
        so_tien_phi_quet_the: soTienPhiQuetThe,
        is_cong_phi_quet_the: true,
      });
      setSelectedHinhThuc(hinhThuc);
      return;
    }

    // Logic cũ cho trường hợp chưa có biên lai
    setSelected([]);
    const isTraTruoc = hinhThuc.hinh_thuc_thanh_toan?.toLowerCase().includes("trả trước");
    setSelectedTienThua({ so_tien: isTraTruoc ? tongTienPhaiNop : 0 });

    if (hinhThuc && props.sf_mucphaithu) {
      const phanBoInfo = hinhThuc.phanBoInfo;
      const conLai = tongTienConLai;

      // Nếu click vào chính hình thức đang được chọn để edit thì không làm gì để tránh mất data đang nhập dở
      if (selected && (selected.sf_hinhthucthanhtoan_id === hinhThuc.id || selected.id && selected.sf_hinhthucthanhtoan_id === hinhThuc.id)) {
        return;
      }

      if (phanBoInfo) {
        // Nếu đã có thông tin phân bổ (đang edit hoặc đã tồn tại), dùng trực tiếp thông tin đó
        const newData = {
          ...hinhThuc,
          ...phanBoInfo,
          // Đảm bảo các field cần thiết có giá trị
          ghi_chu: phanBoInfo.ghi_chu || "",
          ma_chuan_chi: phanBoInfo.ma_chuan_chi || "",
          serial: phanBoInfo.serial || "",
          ngay_thu: phanBoInfo.ngay_thu || moment().format("YYYY-MM-DD HH:mm:ss"),
          sf_mucphaithu_id: props.sf_mucphaithu.id,
          sf_hinhthucthanhtoan_id: hinhThuc.id,
          // Giữ nguyên trạng thái cộng phí của bản ghi cũ
          is_cong_phi_quet_the: !!phanBoInfo.is_cong_phi_quet_the,
          so_tien_phi_quet_the: phanBoInfo.so_tien_phi_quet_the || 0,
        };
        setSelected(newData);
      } else {
        // Nếu là thêm mới hình thức
        const baseSoTien = conLai;
        const soTienPhiQuetThe =
          hinhThuc.loai_hinh_thuc === 3
            ? Math.round(baseSoTien * ((hinhThuc.phi_quet_the || 0) / 100))
            : 0;

        const isCongPhi = true;
        // totalSoTien logic is no longer needed for assignment to so_tien field

        const newData = {
          ...hinhThuc,
          ghi_chu: "",
          ma_chuan_chi: "",
          serial: "",
          ngay_thu: moment().format("YYYY-MM-DD HH:mm:ss"),
          so_tien: baseSoTien, // Use Base only
          so_tien_phi_quet_the: soTienPhiQuetThe,
          sf_mucphaithu_id: props.sf_mucphaithu.id,
          sf_hinhthucthanhtoan_id: hinhThuc.id,
          is_cong_phi_quet_the: isCongPhi,
        };
        setSelected(newData);
      }
    }

    if (props.onHinhThucClick) {
      props.onHinhThucClick(hinhThuc);
    }
  };

  const handleSaveChanges = async () => {
    const hasBienLai = props.sf_bienlaithu && props.sf_bienlaithu.length > 0;

    // Nếu có biên lai: cho sửa ngày thu, ghi chú, phí quẹt thẻ và đổi hình thức; không cho sửa số tiền
    if (hasBienLai && selected.id) {
      setStatus(PageBaseStatus.is_saving);

      if (
        originalHinhThucId !== null &&
        selected.sf_hinhthucthanhtoan_id !== originalHinhThucId
      ) {
        const updatePaymentMethodRes = await phanBoPhiApi.update_payment_method(
          props.sf_mucphaithu.id,
          originalHinhThucId,
          selected.sf_hinhthucthanhtoan_id
        );

        if (!updatePaymentMethodRes.is_success) {
          setStatus(PageBaseStatus.is_completed);
          showNotify({
            message: updatePaymentMethodRes.message || "Cập nhật hình thức thanh toán thất bại",
            type: "warning",
          });
          return;
        }
      }

      const baseSoTien = Number(selected.so_tien) || 0;
      const phiQuetThe = selected?.loai_hinh_thuc === 3
        ? Number(selected.so_tien_phi_quet_the) || 0
        : 0;
      const isCongPhi = !!selected.is_cong_phi_quet_the;
      const tongTien = isCongPhi ? baseSoTien + phiQuetThe : baseSoTien;

      const updatedSelected = {
        ...selected,
        so_tien: tongTien,
        so_tien_phi_quet_the: phiQuetThe,
      };

      const res = await phanBoPhiApi.save_changes(updatedSelected);

      if (res.is_success) {
        setStatus(PageBaseStatus.is_completed);
        showNotify({ message: "Cập nhật thành công", type: "success" });
        resetSelection();
        handleReloadPhanBoPhi();
        handleReloadTienThuaHocSinh();
        if (props.onPhanBoPhiChanged) {
          props.onPhanBoPhiChanged();
        }
      } else {
        setStatus(PageBaseStatus.is_completed);
        showNotify({
          message: res.message || "Cập nhật thất bại",
          type: "warning",
        });
      }
      return;
    }

    // Logic cũ cho trường hợp chưa có biên lai
    if (selected.is_have_ma_chuan_chi && selected.ma_chuan_chi === "") {
      showNotify({
        message: "Vui lòng điền mã chuẩn chi cho hình thức thu quẹt thẻ",
        type: "warning",
      });
      return false;
    }

    setStatus(PageBaseStatus.is_saving);
    let updatedSelected = { ...selected };
    updatedSelected.sf_hinhthucthanhtoan_id = selected.sf_hinhthucthanhtoan_id;
    updatedSelected.hinh_thuc_thanh_toan = selected.hinh_thuc_thanh_toan;

    if (selectedTienThua.so_tien > 0) {
      updatedSelected.so_tien = selectedTienThua.so_tien;
    }

    updatedSelected.so_tien_phi_quet_the =
      selected?.loai_hinh_thuc === 3
        ? Number(updatedSelected.so_tien_phi_quet_the) || 0
        : 0;

    // Tổng tiền = số tiền gốc + phí quẹt thẻ (nếu cộng phí)
    const isCongPhi = !!selected.is_cong_phi_quet_the;
    if (isCongPhi && updatedSelected.so_tien_phi_quet_the > 0) {
      updatedSelected.so_tien = (Number(updatedSelected.so_tien) || 0) + updatedSelected.so_tien_phi_quet_the;
    }

    const res = await phanBoPhiApi.save_changes(updatedSelected);

    if (res.is_success) {
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: "Cập nhật thành công", type: "success" });
      resetSelection();
      handleReloadPhanBoPhi();
      handleReloadTienThuaHocSinh();
      if (props.onPhanBoPhiChanged) {
        props.onPhanBoPhiChanged();
      }
    } else {
      setStatus(PageBaseStatus.is_completed);
      showNotify({
        message: res.message || "Cập nhật thất bại",
        type: "warning",
      });
    }
  };

  const handleClear = async () => {
    const hasBienLai = props.sf_bienlaithu && props.sf_bienlaithu.length > 0;
    resetSelection();
    // if (hasBienLai) {
    //   // Nếu có biên lai, chỉ clear form (không cho xóa)
    //   resetSelection();
    //   return;
    // }

    // // Logic cũ cho trường hợp chưa có biên lai
    // if (selected && selected.sf_hinhthucthanhtoan_id) {
    //   const existingData = sf_phanbophis.find(
    //     (x: any) => x.sf_hinhthucthanhtoan_id === selected.sf_hinhthucthanhtoan_id
    //   );

    //   if (existingData && existingData.id) {
    //     await handleBtnDeleteClicked(existingData.id);
    //   } else {
    //     resetSelection();
    //   }
    // } else {
    //   resetSelection();
    // }
  };

  const handleBtnEditClicked = (data: any) => {
    // Lưu hình thức ban đầu để so sánh
    setOriginalHinhThucId(data.sf_hinhthucthanhtoan_id);

    // Tìm hình thức thanh toán tương ứng
    const hinhThuc = sf_hinhthucthanhtoans.find(
      (x: any) => x.id === data.sf_hinhthucthanhtoan_id
    );

    if (hinhThuc) {
      // Gọi onHinhThucClick để selected hình thức đó
      onHinhThucClick({ ...hinhThuc, phanBoInfo: data });
    } else {
      // Fallback: set selected trực tiếp nếu không tìm thấy hình thức
      setSelected(data);
      setSelectedTienThua({ so_tien: data.so_tien || 0 });
    }
  };

  const handleBtnDeleteClicked = async (id: number) => {
    const res = await phanBoPhiApi.delete(id);
    if (res.is_success) {
      resetSelection();
      await Promise.all([
        handleReloadPhanBoPhi(),
        handleReloadTienThuaHocSinh(),
      ]);
      if (props.onPhanBoPhiChanged) {
        props.onPhanBoPhiChanged();
      }
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: "Xóa thành công", type: "success" });
    } else {
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: res.message || "Xóa thất bại", type: "warning" });
    }
  };

  const getHinhThucInfo = (hinhThucId: number) => {
    return sf_phanbophis?.find(
      (x: any) => x.sf_hinhthucthanhtoan_id === hinhThucId
    );
  };

  const columns: IDataGridColumn[] = [
    {
      caption: "#",
      id: "actions",
      width: 50,
      fixed: "left",
      align: "center",
      cellRender: (data: any) => {
        const hasBienLai = props.sf_bienlaithu && props.sf_bienlaithu.length > 0;

        return (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            {hasBienLai ? (
              <IconButton
                icon={PencilIcon}
                aria-label="Sửa"
                variant="invisible"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleBtnEditClicked(data);
                }}
              />
            ) : (
              <ActionMenu>
                <ActionMenu.Button
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  sx={{ p: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  &nbsp;
                </ActionMenu.Button>
                <ActionMenu.Overlay>
                  <ActionList>
                    <ActionList.Item
                      onSelect={() => handleBtnEditClicked(data)}
                    >
                      <ActionList.LeadingVisual>
                        <PencilIcon />
                      </ActionList.LeadingVisual>
                      Sửa
                    </ActionList.Item>
                    <ActionList.Item
                      variant="danger"
                      onSelect={() => handleBtnDeleteClicked(data.id)}
                    >
                      <ActionList.LeadingVisual>
                        <TrashIcon />
                      </ActionList.LeadingVisual>
                      Xóa
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
          </Box>
        );
      },
    },
    {
      caption: "Hình thức",
      dataField: "hinh_thuc_thanh_toan",
      width: 120,
      align: "left",
      fixed: "left",
      cellRender: (data: any) => {
        return (
          <TextWithEllipse
            text={data.hinh_thuc_thanh_toan || ""}
            lineNumber={1}
          />
        );
      },
    },
    {
      caption: "Số tiền",
      dataField: "so_tien",
      width: 100,
      align: "right",
      cellRender: (data: any) => {
        const row = data as PhanBoPhiGridItem;
        const hinhThuc = sf_hinhthucthanhtoans.find(
          (x) => x.id === row.sf_hinhthucthanhtoan_id
        );
        const loaiHinhThuc = row.loai_hinh_thuc ?? hinhThuc?.loai_hinh_thuc;
        const isCreditCard = loaiHinhThuc === 3;
        const isCongPhiRow = !!row.is_cong_phi_quet_the;
        const phiQuetTheValue = isCongPhiRow ? (row.so_tien_phi_quet_the ?? row.phi_quet_the ?? 0) : 0;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", textAlign: "right" }}>
            <Box sx={{ textAlign: "right" }}>
              {((row.so_tien || 0) + (phiQuetTheValue || 0)).toLocaleString()}
            </Box>
            {row.ngay_thu && (
              <Box sx={{ fontSize: "0.9em", color: "#6c757d", opacity: 0.8, textAlign: "right" }}>
                {moment(row.ngay_thu).format("DD/MM/YYYY")}
              </Box>
            )}
            {isCreditCard && isCongPhiRow && (
              <Box sx={{ fontSize: "0.9em", color: "danger.fg", opacity: 0.8, textAlign: "right", mt: 0.5 }}>
                Phí quẹt thẻ: {(phiQuetTheValue || 0).toLocaleString()}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      caption: "Ghi chú",
      dataField: "ghi_chu",
      width: 200,
      align: "left",
      cellRender: (data: any) => {
        return (
          <TextWithEllipse
            text={data.ghi_chu || ""}
            lineNumber={1}
          />
        );
      },
    },

  ];


  return (
    <Box sx={{ p: 0.5 }}>
      <Box
        sx={{
          borderRadius: 2,
          p: 1,
          bg: "canvas.default",
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              borderRadius: 1,
              p: 1,
              mb: 1,
              backgroundColor: "canvas.subtle",
              border: "1px solid",
              borderColor: "border.subtle",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box
                  as="span"
                  sx={{ fontWeight: "medium", color: "fg.default", flexShrink: 0 }}
                >
                  Nội dung:
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextInput
                      size="small"
                      block
                      disabled={!isEditingNoiDung}
                      value={draftNoiDung}
                      onChange={(e) => {
                        setDraftNoiDung(e.target.value || "");
                      }}
                    />
                  </Box>
                  {!isEditingNoiDung ? (
                    <IconButton
                      icon={PencilIcon}
                      aria-label="Sửa nội dung"
                      variant="default"
                      size="small"
                      onClick={() => {
                        setDraftNoiDung(props.sf_mucphaithu?.noi_dung || "");
                        setIsEditingNoiDung(true);
                      }}
                    />
                  ) : (
                    <>
                      <IconButton
                        icon={XIcon}
                        aria-label="Đóng chỉnh sửa nội dung"
                        variant="default"
                        size="small"
                        onClick={() => {
                          setDraftNoiDung(props.sf_mucphaithu?.noi_dung || "");
                          setIsEditingNoiDung(false);
                        }}
                      />
                      <IconButton
                        icon={CheckIcon}
                        aria-label="Cập nhật nội dung"
                        variant="primary"
                        size="small"
                        onClick={async () => {
                          const newNoiDung = draftNoiDung || "";
                          if (props.onNoiDungChange) {
                            props.onNoiDungChange(newNoiDung);
                          }
                          if (props.onUpdateNoiDung) {
                            await props.onUpdateNoiDung(newNoiDung);
                          }
                          setIsEditingNoiDung(false);
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                  Ngày thu:
                </Box>
                <Box sx={{ minWidth: 150, display: "flex", alignItems: "center", gap: 1 }}>
                  <DateInput
                    value={
                      draftNgayThu
                        ? moment(
                          draftNgayThu,
                          ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
                          true
                        ).format("DD/MM/YYYY")
                        : ""
                    }
                    size="small"
                    width={150}
                    onValueChanged={(value) => {
                      const nextIso = value
                        ? moment(value, "DD/MM/YYYY", true).format("YYYY-MM-DD")
                        : "";
                      setDraftNgayThu(nextIso);
                    }}
                  />
                  {!isEditingNgayThu ? (
                    <IconButton
                      icon={PencilIcon}
                      aria-label="Sửa ngày thu"
                      variant="default"
                      size="small"
                      onClick={() => {
                        const isoValue = props.ngaythu ? moment(props.ngaythu).format("YYYY-MM-DD") : "";
                        setDraftNgayThu(isoValue);
                        setIsEditingNgayThu(true);
                      }}
                    />
                  ) : (
                    <>
                      <IconButton
                        icon={XIcon}
                        aria-label="Đóng chỉnh sửa ngày thu"
                        variant="default"
                        size="small"
                        onClick={() => {
                          const isoValue = props.ngaythu ? moment(props.ngaythu).format("YYYY-MM-DD") : "";
                          setDraftNgayThu(isoValue);
                          setIsEditingNgayThu(false);
                        }}
                      />
                      <IconButton
                        icon={CheckIcon}
                        aria-label="Cập nhật ngày thu"
                        variant="primary"
                        size="small"
                        onClick={() => {
                          const fullDate = moment(draftNgayThu, "YYYY-MM-DD", true).format("YYYY-MM-DD HH:mm:ss");
                          if (props.onNgayThuChange) {
                            props.onNgayThuChange(fullDate);
                          }
                          if (props.onUpdateNgayThu) {
                            props.onUpdateNgayThu(fullDate);
                          }
                          setIsEditingNgayThu(false);
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                  Người thu:
                </Box>
                <Box
                  as="span"
                  sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
                >
                  {props.sf_bienlaithu?.[0]?.username || ""}
                </Box>
              </Box>

            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {/* <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Số tiền thừa:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "success.fg", fontSize: 13 }}
              >
                {soTienThuaHienThi.toLocaleString()}
              </Box> */}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng định mức:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienDinhMuc.toLocaleString()}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng miễn giảm:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienMienGiam.toLocaleString()}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng phải nộp:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienPhaiNop.toLocaleString()}
              </Box>
            </Box>
            {sf_phanbophis && sf_phanbophis.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box
                  as="span"
                  sx={{ fontWeight: "medium", color: "fg.default" }}
                >
                  Đã nộp:
                </Box>
                <Box
                  as="span"
                  sx={{ fontWeight: "700", color: "accent.fg", fontSize: 13 }}
                >
                  {tongDaNopPhanBo.toLocaleString()}
                </Box>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Phí quẹt thẻ:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "danger.fg", fontSize: 13 }}
              >
                {tongPhiQuetThe > 0 ? tongPhiQuetThe.toLocaleString() : "0"}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Còn lại:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "danger.fg", fontSize: 13 }}
              >
                {tongTienConLai > 0 ? tongTienConLai.toLocaleString() : "0"}
              </Box>
            </Box>
          </Box>
        </Box>

        {(() => {
          const hasBienLai = props.sf_bienlaithu && props.sf_bienlaithu.length > 0;

          // Nếu có biên lai, chỉ hiển thị khi đang edit (selected.id có giá trị)
          if (hasBienLai && !selected?.id) {
            return null;
          }

          return (
            <>
              <Box
                as="h3"
                sx={{
                  fontSize: 1,
                  fontWeight: "bold",
                  mt: 1,
                  mb: 1,
                  borderBottom: "1px solid",
                  borderColor: "border.subtle",
                  pb: 0.5,
                  textAlign: "center",
                  color: "fg.default",
                }}
              >
                Hình thức thanh toán
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  // maxHeight: "280px",
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {sf_hinhthucthanhtoans.map((x: any) => {
                  const phanBoInfo = getHinhThucInfo(x.id);
                  const isSelected =
                    selectedHinhThuc?.id === x.id ||
                    selectedHinhThuc?.sf_hinhthucthanhtoan_id === x.id;

                  return (
                    <Box
                      key={x.id}
                      onClick={() => onHinhThucClick({ ...x, phanBoInfo })}
                      className={`hinh-thuc-item ${isSelected ? "selected" : ""}`}
                      sx={{
                        flex: "1 1 calc(33.333% - 6px)",
                        minWidth: "calc(33.333% - 6px)",
                        boxSizing: "border-box",
                        borderRadius: 2,
                        border: isSelected ? "1px solid" : undefined,
                        borderColor: isSelected ? "accent.emphasis" : undefined,
                        bg: isSelected ? "accent.subtle" : undefined,
                      }}
                    >
                      {x.img ? (
                        <Box
                          as="img"
                          src={x.img}
                          alt="Hình thức"
                          sx={{
                            width: "auto",
                            height: 18,
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            mr: 1.5,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 0,
                            bg: isSelected ? "accent.muted" : "canvas.subtle",
                            border: "1px solid",
                            borderColor: isSelected ? "accent.emphasis" : "border.default",
                            borderRadius: 1,
                          }}
                        >
                          💳
                        </Box>
                      )}
                      <Box
                        as="span"
                        sx={{
                          flex: 1,
                          fontSize: 0,
                          fontWeight: "medium",
                          color: isSelected ? "accent.fg" : "fg.default",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflow: "visible",
                          textOverflow: "clip",
                          textAlign: "left",
                          lineHeight: 1.35,
                        }}
                      >
                        {x.hinh_thuc_thanh_toan}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          );
        })()}
        {selected && selected.hinh_thuc_thanh_toan && (
          <>
            <Box
              as="hr"
              sx={{
                my: 1,
                border: "none",
                borderTop: "1px solid",
                borderColor: "border.default",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: 1,
                borderRadius: 2,
                bg: "canvas.subtle",
              }}
            >
              <Box sx={{ fontSize: 0, fontWeight: 600, color: "fg.default" }}>
                {selected.hinh_thuc_thanh_toan}
              </Box>
              {selected.hinh_thuc_thanh_toan
                ?.toLowerCase()
                .includes("trả trước") ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          gap: 0.5,
                        }}
                      >
                        <Box
                          as="label"
                          sx={{
                            margin: 0,
                            fontWeight: "medium",
                            fontSize: 0,
                            color: "fg.default",
                          }}
                        >
                          Số tiền:
                        </Box>
                        <NumberInput
                          min={0}
                          max={
                            tong_tien_thua - tong_tien_tra_truoc <= 0
                              ? 0
                              : tong_tien_thua - tong_tien_tra_truoc
                          }
                          value={hasBienLai ? selected.so_tien || 0 : selectedTienThua.so_tien || 0}
                          readOnly={hasBienLai}
                          onChange={(e) => {
                            if (hasBienLai) {
                              return;
                            }
                            setSelectedTienThua({
                              ...selectedTienThua,
                              so_tien: Number(e.target.value),
                            });
                          }}
                        />
                      </Box>
                      {!hasBienLai && (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                              gap: 0.5,
                            }}
                          >
                            <Box
                              as="label"
                              sx={{
                                margin: 0,
                                fontWeight: "medium",
                                fontSize: 0,
                                color: "fg.default",
                              }}
                            >
                              Tiền thừa:
                            </Box>
                            <NumberInput
                              min={0}
                              value={
                                tong_tien_thua - tong_tien_tra_truoc < 0
                                  ? 0
                                  : tong_tien_thua - tong_tien_tra_truoc
                              }
                              readOnly
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                              gap: 0.5,
                            }}
                          >
                            <Box
                              as="label"
                              sx={{
                                margin: 0,
                                fontWeight: "medium",
                                fontSize: 0,
                                color: "fg.default",
                              }}
                            >
                              Số tiền còn lại:
                            </Box>
                            <NumberInput
                              min={0}
                              value={
                                tong_tien_thua_temp - tong_tien_tra_truoc < 0
                                  ? 0
                                  : tong_tien_thua_temp - tong_tien_tra_truoc
                              }
                              readOnly
                            />
                          </Box>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "33.333%", // Match width of Số tiền when sharing with Note
                          gap: 0.5,
                        }}
                      >
                        <Box
                          as="label"
                          sx={{
                            margin: 0,
                            fontWeight: "medium",
                            fontSize: 0,
                            color: "fg.default",
                          }}
                        >
                          Ngày thu:
                        </Box>
                        <TextInput
                          type="date"
                          block
                          value={
                            selected.ngay_thu
                              ? moment(selected.ngay_thu).format("YYYY-MM-DD")
                              : ""
                          }
                          onChange={(e) => {
                            const fullDate = moment(e.target.value).format(
                              "YYYY-MM-DD HH:mm:ss"
                            );
                            setSelected({ ...selected, ngay_thu: fullDate });
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          gap: 0.5,
                        }}
                      >
                        <Box
                          as="label"
                          sx={{
                            margin: 0,
                            fontWeight: "medium",
                            fontSize: 0,
                            color: "fg.default",
                          }}
                        >
                          Ghi chú:
                        </Box>
                        <TextInput
                          block
                          value={selected.ghi_chu || ""}
                          onChange={(e) => {
                            setSelected({ ...selected, ghi_chu: e.target.value });
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {(() => {
                    const isCard = selected.loai_hinh_thuc === 3;
                    const rate = (selected.phi_quet_the || 0) / 100;

                    const isCongPhi = !!selected.is_cong_phi_quet_the;

                    const revenueAmount = Number(selected.so_tien) || 0;
                    const feeAmount = Number(selected.so_tien_phi_quet_the) || 0;

                    const totalToDisplay = isCard && isCongPhi ? revenueAmount + feeAmount : revenueAmount;

                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                            <Box as="label" sx={{ margin: 0, fontWeight: "medium", fontSize: 0, color: "fg.default" }}>
                              Số tiền:
                            </Box>
                            <NumberInput
                              min={0}
                              value={revenueAmount}
                              readOnly={hasBienLai}
                              onChange={(e) => {
                                if (hasBienLai) return;
                                const newBase = Number(e.target.value);
                                const newFee = Math.round(newBase * rate);
                                setSelected({
                                  ...selected,
                                  so_tien: newBase,
                                  so_tien_phi_quet_the: newFee,
                                });
                              }}
                            />
                          </Box>

                          {isCard && (
                            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box as="label" sx={{ margin: 0, fontWeight: "medium", fontSize: 0, color: "fg.default" }}>
                                  Phí quẹt thẻ:
                                </Box>
                                <FormControl>
                                  <Checkbox
                                    checked={isCongPhi}
                                    disabled={hasBienLai}
                                    onChange={(e) => {
                                      if (hasBienLai) return;
                                      setSelected({
                                        ...selected,
                                        is_cong_phi_quet_the: !!e.target.checked,
                                      });
                                    }}
                                  />
                                  <FormControl.Label sx={{ fontSize: 0, fontWeight: "normal", mr: 2 }}>Cộng phí QT</FormControl.Label>
                                </FormControl>
                              </Box>
                              <NumberInput
                                min={0}
                                value={feeAmount}
                                readOnly={hasBienLai}
                                onChange={(e) => {
                                  if (hasBienLai) return;
                                  const newFee = Number(e.target.value);
                                  setSelected({
                                    ...selected,
                                    so_tien_phi_quet_the: newFee,
                                  });
                                }}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                            <Box as="label" sx={{ margin: 0, fontWeight: "medium", fontSize: 0, color: "fg.default" }}>
                              Tổng cộng:
                            </Box>
                            <NumberInput
                              min={0}
                              value={totalToDisplay}
                              readOnly={true}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", width: isCard ? "33.333%" : "50%", gap: 0.5 }}>
                            <Box as="label" sx={{ margin: 0, fontWeight: "medium", fontSize: 0, color: "fg.default" }}>
                              Ngày thu:
                            </Box>
                            <TextInput
                              type="date"
                              block
                              value={selected.ngay_thu ? moment(selected.ngay_thu).format("YYYY-MM-DD") : ""}
                              onChange={(e) => {
                                const fullDate = moment(e.target.value).format("YYYY-MM-DD HH:mm:ss");
                                setSelected({ ...selected, ngay_thu: fullDate });
                              }}
                            />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
                            <Box as="label" sx={{ margin: 0, fontWeight: "medium", fontSize: 0, color: "fg.default" }}>
                              Ghi chú:
                            </Box>
                            <TextInput
                              block
                              value={selected.ghi_chu || ""}
                              onChange={(e) => {
                                setSelected({ ...selected, ghi_chu: e.target.value });
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()}
                </>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 0.5,
                  mt: 0.5,
                }}
              >
                {(() => {
                  const hasBienLai = props.sf_bienlaithu && props.sf_bienlaithu.length > 0;
                  const existingData = selected && selected.sf_hinhthucthanhtoan_id
                    ? sf_phanbophis.find(
                      (x: any) =>
                        x.sf_hinhthucthanhtoan_id ===
                        selected.sf_hinhthucthanhtoan_id
                    )
                    : null;
                  const hasExistingData = !!existingData;

                  // Nếu có biên lai, chỉ hiển thị nút Hủy và Cập nhật (không có xóa)
                  if (hasBienLai) {
                    return (
                      <>
                        <Button
                          variant="default"
                          leadingVisual={XIcon}
                          text="Hủy"
                          disabled={!selected}
                          onClick={handleClear}
                        />
                        <Button
                          variant="primary"
                          leadingVisual={CheckIcon}
                          text="Cập nhật"
                          disabled={!selected || !selected.id}
                          isLoading={status === PageBaseStatus.is_saving}
                          onClick={handleSaveChanges}
                        />
                      </>
                    );
                  }

                  // Logic cũ cho trường hợp chưa có biên lai
                  const clearIcon = hasExistingData ? TrashIcon : XIcon;
                  return (
                    <>
                      <Button
                        variant={"default"}
                        leadingVisual={XIcon}
                        text={"Hủy"}
                        disabled={!selected}
                        isLoading={status === PageBaseStatus.is_saving}
                        onClick={handleClear}
                      />
                      <Button
                        variant="primary"
                        leadingVisual={CheckIcon}
                        text={hasExistingData ? "Cập nhật" : "Thêm mới"}
                        disabled={
                          !selected ||
                          (selected.hinh_thuc_thanh_toan
                            ?.toLowerCase()
                            .includes("trả trước")
                            ? selectedTienThua?.so_tien === 0 ||
                            !selectedTienThua?.so_tien
                            : selected.so_tien === 0 || !selected.so_tien)
                        }
                        isLoading={status === PageBaseStatus.is_saving}
                        onClick={handleSaveChanges}
                      />
                    </>
                  );
                })()}
              </Box>
            </Box>
          </>
        )}
        <Box sx={{ mt: 1 }}>
          <DataGrid
            dataSource={sf_phanbophis}
            columns={columns}
            paging={{ enable: false }}
            filterRow={{ enable: false }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ThuPhiThongTinTongHop;
